/**
 * Dialog for creating new employee absences
 * Includes form validation, employee selection, and recurring absence support
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react';
import { format, addDays, addWeeks, addMonths } from 'date-fns';
import { Info } from 'lucide-react';
import { formatDateDisplay, formatWeekday } from '@/lib/format';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { TimePicker } from '@/components/ui/time-picker';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Loader2, Repeat } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmployeeSelect } from '@/components/shifts/EmployeeSelect';
import { AbsenceTypeBadge } from './AbsenceTypeBadge';
import {
  useCreateEmployeeAbsence,
  useCreateRecurringAbsence,
} from '@/hooks/useEmployeeAbsences';
import { useVacationBalance } from '@/hooks/useWorkforce';
import { ApiError } from '@/api';
import { AbsenceType, AbsenceRecurrenceType } from '@/types/employeeAbsences';
import {
  getCreateAbsenceSchema,
  toCreateAbsenceRequest,
  type CreateAbsenceFormData,

  type CreateAbsenceFormInput,
} from '@/schemas/absenceSchemas';

export interface CreateAbsenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: Date;
  defaultEmployeeId?: string;
}

// Weekday numbers: 1=Monday, 2=Tuesday, ..., 7=Sunday
const WEEKDAY_VALUES = [1, 2, 3, 4, 5, 6, 7] as const;

/** Returns a translated weekday abbreviation. All IDs are static for lingui extraction. */
function getWeekdayLabel(i18n: { _: (descriptor: { id: string; message: string }) => string }, day: number): string {
  switch (day) {
    case 1: return i18n._({ id: 'common.weekday.monday', message: 'Mon' });
    case 2: return i18n._({ id: 'common.weekday.tuesday', message: 'Tue' });
    case 3: return i18n._({ id: 'common.weekday.wednesday', message: 'Wed' });
    case 4: return i18n._({ id: 'common.weekday.thursday', message: 'Thu' });
    case 5: return i18n._({ id: 'common.weekday.friday', message: 'Fri' });
    case 6: return i18n._({ id: 'common.weekday.saturday', message: 'Sat' });
    case 7: return i18n._({ id: 'common.weekday.sunday', message: 'Sun' });
    default: return '';
  }
}

/**
 * Generates a preview of the next N occurrence dates for a recurring pattern.
 * Used to show users what dates will be generated before they confirm.
 */
function generatePreviewDates(
  startDate: Date,
  recurrenceType: string,
  recurrenceDays: number[],
  recurrenceInterval: number,
  endDate: Date | null,
  maxCount: number = 5
): Date[] {
  const dates: Date[] = [];
  const end = endDate || addMonths(startDate, 6);

  // Map recurrence day numbers (1=Mon...7=Sun) to JS getDay() (0=Sun...6=Sat)
  const targetJsDays = new Set(
    recurrenceDays.map((d) => (d === 7 ? 0 : d))
  );

  if (
    recurrenceType === AbsenceRecurrenceType.WEEKLY ||
    recurrenceType === AbsenceRecurrenceType.BI_WEEKLY ||
    recurrenceType === AbsenceRecurrenceType.CUSTOM
  ) {
    const weekInterval =
      recurrenceType === AbsenceRecurrenceType.WEEKLY
        ? 1
        : recurrenceType === AbsenceRecurrenceType.BI_WEEKLY
          ? 2
          : recurrenceInterval;

    let current = startDate;
    let weekStart = startDate;

    while (current <= end && dates.length < maxCount) {
      if (targetJsDays.has(current.getDay())) {
        dates.push(new Date(current));
      }
      current = addDays(current, 1);

      if (weekInterval > 1) {
        const daysSinceStart = Math.floor(
          (current.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000)
        );
        if (daysSinceStart >= 7) {
          current = addWeeks(weekStart, weekInterval);
          weekStart = current;
        }
      }
    }
  } else if (recurrenceType === AbsenceRecurrenceType.MONTHLY) {
    let current = startDate;
    while (current <= end && dates.length < maxCount) {
      dates.push(new Date(current));
      current = addMonths(current, recurrenceInterval);
    }
  }

  return dates;
}

/**
 * Dialog component for creating new employee absences.
 * Supports both one-time and recurring absence creation.
 */
export function CreateAbsenceDialog({
  open,
  onOpenChange,
  defaultDate,
  defaultEmployeeId,
}: CreateAbsenceDialogProps) {
  const { i18n } = useLingui();
  const createAbsence = useCreateEmployeeAbsence();
  const createRecurringAbsence = useCreateRecurringAbsence();

  // Recurring absence state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<string>(
    AbsenceRecurrenceType.WEEKLY
  );
  const [recurrenceDays, setRecurrenceDays] = useState<number[]>([]);
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(null);
  const [submitAsRequest, setSubmitAsRequest] = useState(false);

  // Form setup with Zod validation
  const schema = useMemo(() => getCreateAbsenceSchema(i18n), [i18n]);
  const form = useForm<CreateAbsenceFormInput, unknown, CreateAbsenceFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      employeeId: defaultEmployeeId || '',
      absenceType: AbsenceType.VACATION,
      fromDate: defaultDate || new Date(),
      toDate: defaultDate || new Date(),
      fullDay: true,
      fromTime: '08:00',
      toTime: '16:00',
      notes: '',
    },
  });

  // Watch fields for conditional rendering
  const fullDay = useWatch({ control: form.control, name: 'fullDay' });
  const absenceType = useWatch({ control: form.control, name: 'absenceType' });
  const employeeId = useWatch({ control: form.control, name: 'employeeId' });

  // Watch dates for synchronization
  const fromDate = useWatch({ control: form.control, name: 'fromDate' });
  const toDate = useWatch({ control: form.control, name: 'toDate' });

  // Fetch vacation balance when absence type is VACATION and employee is selected
  const currentYear = fromDate ? fromDate.getFullYear() : new Date().getFullYear();
  const isVacationType = absenceType === AbsenceType.VACATION;
  const vacationBalance = useVacationBalance(
    isVacationType && employeeId ? employeeId : '',
    currentYear
  );
  const isVacationBalanceNotFound =
    vacationBalance.isError &&
    vacationBalance.error instanceof ApiError &&
    vacationBalance.error.status === 404;

  // Track previous dates to detect which field changed
  const prevFromDateRef = useRef<Date | undefined>(fromDate);
  const prevToDateRef = useRef<Date | undefined>(toDate);

  // Synchronize dates when they become inconsistent
  useEffect(() => {
    if (!fromDate || !toDate) return;

    const fromChanged = prevFromDateRef.current?.getTime() !== fromDate.getTime();
    const toChanged = prevToDateRef.current?.getTime() !== toDate.getTime();

    // Only synchronize if dates are out of order
    if (fromDate > toDate) {
      if (fromChanged) {
        // User moved fromDate forward, sync toDate to match
        form.setValue('toDate', fromDate, { shouldValidate: true });
      } else if (toChanged) {
        // User moved toDate backward, sync fromDate to match
        form.setValue('fromDate', toDate, { shouldValidate: true });
      }
    }

    // Update refs after processing
    prevFromDateRef.current = fromDate;
    prevToDateRef.current = toDate;
  }, [fromDate, toDate, form]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        employeeId: defaultEmployeeId || '',
        absenceType: AbsenceType.VACATION,
        fromDate: defaultDate || new Date(),
        toDate: defaultDate || new Date(),
        fullDay: true,
        fromTime: '08:00',
        toTime: '16:00',
        notes: '',
      });
      setIsRecurring(false);
      setRecurrenceType(AbsenceRecurrenceType.WEEKLY);
      setRecurrenceDays([]);
      setRecurrenceInterval(1);
      setRecurrenceEndDate(null);
      setSubmitAsRequest(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDate, defaultEmployeeId]);

  // Toggle a weekday in the recurrence days array
  const toggleRecurrenceDay = useCallback((day: number) => {
    setRecurrenceDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  }, []);

  // Generate preview dates for the recurring pattern
  const previewDates = useMemo(() => {
    if (!isRecurring || !fromDate) return [];
    if (
      recurrenceType !== AbsenceRecurrenceType.MONTHLY &&
      recurrenceDays.length === 0
    ) {
      return [];
    }
    return generatePreviewDates(
      fromDate,
      recurrenceType,
      recurrenceDays,
      recurrenceInterval,
      recurrenceEndDate
    );
  }, [isRecurring, fromDate, recurrenceType, recurrenceDays, recurrenceInterval, recurrenceEndDate]);

  // Handle form submission
  const handleSubmit = async (data: CreateAbsenceFormData) => {
    try {
      if (isRecurring) {
        // Validate recurrence days for non-monthly types
        if (
          recurrenceType !== AbsenceRecurrenceType.MONTHLY &&
          recurrenceDays.length === 0
        ) {
          return;
        }

        await createRecurringAbsence.mutateAsync({
          employee_id: data.employeeId,
          absence_type: data.absenceType,
          recurrence_type: recurrenceType,
          recurrence_days:
            recurrenceType === AbsenceRecurrenceType.MONTHLY
              ? []
              : recurrenceDays,
          recurrence_interval: recurrenceInterval,
          full_day: data.fullDay,
          from_time: !data.fullDay ? data.fromTime : undefined,
          to_time: !data.fullDay ? data.toTime : undefined,
          start_date: format(data.fromDate, 'yyyy-MM-dd'),
          end_date: recurrenceEndDate
            ? format(recurrenceEndDate, 'yyyy-MM-dd')
            : undefined,
          notes: data.notes || undefined,
          submit_as_request: submitAsRequest,
        });
      } else {
        const request = toCreateAbsenceRequest(data);
        await createAbsence.mutateAsync({
          ...request,
          submit_as_request: submitAsRequest || undefined,
        });
      }
      onOpenChange(false);
    } catch {
      // Error handling is done in the mutation hooks
    }
  };

  const isPending = createAbsence.isPending || createRecurringAbsence.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'absences.dialog.create.title', message: 'Create Absence' })}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Employee Select */}
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'absences.form.employee', message: 'Employee' })}
                  </FormLabel>
                  <FormControl>
                    <EmployeeSelect
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                      id="employee-select"
                      aria-describedby="employee-error"
                    />
                  </FormControl>
                  <FormMessage id="employee-error" />
                </FormItem>
              )}
            />

            {/* Absence Type */}
            <FormField
              control={form.control}
              name="absenceType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'absences.form.absenceType', message: 'Absence Type' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={AbsenceType.VACATION}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.VACATION} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.SICK}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.SICK} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.PERSONAL}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.PERSONAL} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.TRAINING}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.TRAINING} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.COMP_TIME}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.COMP_TIME} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.ACCIDENT}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.ACCIDENT} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.CHILD_SICK}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.CHILD_SICK} />
                        </div>
                      </SelectItem>
                      <SelectItem value={AbsenceType.OTHER}>
                        <div className="flex items-center gap-2">
                          <AbsenceTypeBadge type={AbsenceType.OTHER} />
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Vacation Balance Info */}
            {isVacationType && employeeId && (
              <Alert variant="info">
                <Info className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>
                  {isVacationBalanceNotFound ? (
                    <span className="text-sm text-muted-foreground">
                      {i18n._({
                        id: 'absences.vacationBalance.notConfigured',
                        message: 'No vacation balance configured for this year',
                      })}
                    </span>
                  ) : vacationBalance.data ? (
                    <div className="text-sm">
                      <span className="font-medium">
                        {i18n._({
                          id: 'absences.vacationBalance.title',
                          message: 'Vacation Balance ({year})',
                          values: { year: currentYear },
                        })}
                      </span>
                      <div className="mt-1 text-muted-foreground">
                        {vacationBalance.data.entitlement_days}{' '}
                        {i18n._({
                          id: 'absences.vacationBalance.entitlement',
                          message: 'entitlement',
                        })}
                        {' + '}
                        {vacationBalance.data.carry_over_days ?? 0}{' '}
                        {i18n._({
                          id: 'absences.vacationBalance.carryOver',
                          message: 'carry-over',
                        })}
                        {' + '}
                        {vacationBalance.data.manual_adjustment ?? 0}{' '}
                        {i18n._({
                          id: 'absences.vacationBalance.adjustment',
                          message: 'adjustment',
                        })}
                        {' = '}
                        <span className="font-semibold text-foreground">
                          {(vacationBalance.data.entitlement_days ?? 0) +
                            (vacationBalance.data.carry_over_days ?? 0) +
                            (vacationBalance.data.manual_adjustment ?? 0)}{' '}
                          {i18n._({
                            id: 'absences.vacationBalance.totalDays',
                            message: 'total days',
                          })}
                        </span>
                      </div>
                    </div>
                  ) : vacationBalance.isLoading ? (
                    <span className="text-sm text-muted-foreground">
                      {i18n._({
                        id: 'common.loading',
                        message: 'Loading...',
                      })}
                    </span>
                  ) : null}
                </AlertDescription>
              </Alert>
            )}

            {/* Date Fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fromDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'absences.form.fromDate', message: 'From Date' })}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={i18n._({
                          id: 'common.selectDate',
                          message: 'Select date',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="toDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'absences.form.toDate', message: 'To Date' })}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={i18n._({
                          id: 'common.selectDate',
                          message: 'Select date',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Full Day Toggle */}
            <FormField
              control={form.control}
              name="fullDay"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>
                      {i18n._({ id: 'absences.form.fullDay', message: 'Full Day' })}
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Time Fields - only shown when fullDay is false */}
            {!fullDay && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fromTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {i18n._({ id: 'absences.form.fromTime', message: 'From Time' })}
                      </FormLabel>
                      <FormControl>
                        <TimePicker value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="toTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {i18n._({ id: 'absences.form.toTime', message: 'To Time' })}
                      </FormLabel>
                      <FormControl>
                        <TimePicker value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'absences.form.notes', message: 'Notes' })}
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} maxLength={500} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Recurring Toggle */}
            <div className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="recurring-toggle">
                  {i18n._({
                    id: 'absences.recurring.toggle',
                    message: 'Make recurring',
                  })}
                </Label>
              </div>
              <Switch
                id="recurring-toggle"
                checked={isRecurring}
                onCheckedChange={setIsRecurring}
              />
            </div>

            {/* Recurring Settings - visible when toggle is on */}
            {isRecurring && (
              <div className="space-y-4 rounded-md border p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {i18n._({
                      id: 'absences.recurring.title',
                      message: 'Recurring Absence',
                    })}
                  </span>
                </div>

                {/* Recurrence Type */}
                <div className="space-y-1.5">
                  <Label htmlFor="recurrence-type-select">
                    {i18n._({
                      id: 'absences.recurring.type',
                      message: 'Recurrence',
                    })}
                  </Label>
                  <Select
                    value={recurrenceType}
                    onValueChange={setRecurrenceType}
                  >
                    <SelectTrigger id="recurrence-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AbsenceRecurrenceType.WEEKLY}>
                        {i18n._({
                          id: 'absences.recurring.weekly',
                          message: 'Weekly',
                        })}
                      </SelectItem>
                      <SelectItem value={AbsenceRecurrenceType.BI_WEEKLY}>
                        {i18n._({
                          id: 'absences.recurring.biWeekly',
                          message: 'Bi-weekly',
                        })}
                      </SelectItem>
                      <SelectItem value={AbsenceRecurrenceType.MONTHLY}>
                        {i18n._({
                          id: 'absences.recurring.monthly',
                          message: 'Monthly',
                        })}
                      </SelectItem>
                      <SelectItem value={AbsenceRecurrenceType.CUSTOM}>
                        {i18n._({
                          id: 'absences.recurring.custom',
                          message: 'Custom',
                        })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Interval - only shown for CUSTOM type */}
                {recurrenceType === AbsenceRecurrenceType.CUSTOM && (
                  <div className="space-y-1.5">
                    <Label htmlFor="recurrence-interval">
                      {i18n._({
                        id: 'absences.recurring.interval',
                        message: 'Every {count} weeks',
                        values: { count: recurrenceInterval },
                      })}
                    </Label>
                    <Input
                      id="recurrence-interval"
                      type="number"
                      min={1}
                      max={52}
                      value={recurrenceInterval}
                      onChange={(e) =>
                        setRecurrenceInterval(
                          Math.max(1, Math.min(52, parseInt(e.target.value, 10) || 1))
                        )
                      }
                    />
                  </div>
                )}

                {/* Weekday Selection - not shown for MONTHLY */}
                {recurrenceType !== AbsenceRecurrenceType.MONTHLY && (
                  <div className="space-y-1.5">
                    <Label>
                      {i18n._({
                        id: 'absences.recurring.days',
                        message: 'Days',
                      })}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {WEEKDAY_VALUES.map((dayValue) => (
                        <label
                          key={dayValue}
                          className="flex items-center gap-1.5 cursor-pointer"
                        >
                          <Checkbox
                            checked={recurrenceDays.includes(dayValue)}
                            onCheckedChange={() => toggleRecurrenceDay(dayValue)}
                          />
                          <span className="text-sm">
                            {getWeekdayLabel(i18n, dayValue)}
                          </span>
                        </label>
                      ))}
                    </div>
                    {recurrenceDays.length === 0 && (
                      <p className="text-xs text-destructive">
                        {i18n._({
                          id: 'absences.recurring.daysRequired',
                          message: 'Select at least one day',
                        })}
                      </p>
                    )}
                  </div>
                )}

                {/* End Date */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>
                      {i18n._({
                        id: 'absences.recurring.endDate',
                        message: 'End Date',
                      })}
                    </Label>
                    {recurrenceEndDate && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto py-0 px-1 text-xs text-muted-foreground"
                        onClick={() => setRecurrenceEndDate(null)}
                      >
                        {i18n._({
                          id: 'absences.recurring.openEnded',
                          message: 'Open-ended',
                        })}
                      </Button>
                    )}
                  </div>
                  <DatePicker
                    value={recurrenceEndDate ?? undefined}
                    onChange={(date) => setRecurrenceEndDate(date ?? null)}
                    placeholder={i18n._({
                      id: 'absences.recurring.openEnded',
                      message: 'Open-ended',
                    })}
                  />
                </div>

                {/* Preview of next occurrences */}
                {previewDates.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      {i18n._({
                        id: 'absences.recurring.preview',
                        message: 'Next occurrences',
                      })}
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {previewDates.map((date, idx) => (
                        <span
                          key={idx}
                          className="inline-block rounded-md bg-muted px-2 py-0.5 text-xs"
                        >
                          {formatWeekday(date, i18n.locale, 'short')} {formatDateDisplay(date)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit as Request toggle */}
            <div className="flex flex-row items-center justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="submit-as-request">
                  {i18n._({
                    id: 'absences.approval.submitRequest',
                    message: 'Submit as Request',
                  })}
                </Label>
                <p className="text-xs text-muted-foreground" id="submit-as-request-description">
                  {submitAsRequest
                    ? i18n._({
                        id: 'absences.approval.submitRequestOn',
                        message: 'Saved as a request. It takes effect once someone approves it in the approval queue.',
                      })
                    : i18n._({
                        id: 'absences.approval.submitRequestOff',
                        message: 'Saved as approved right away. Planned shifts on these days are cancelled and the employee is notified.',
                      })}
                </p>
              </div>
              <Switch
                id="submit-as-request"
                checked={submitAsRequest}
                onCheckedChange={setSubmitAsRequest}
                aria-describedby="submit-as-request-description"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {i18n._({ id: 'common.cancel', message: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {i18n._({ id: 'common.create', message: 'Create' })}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
